import * as React from "react"
import Box from "@mui/material/Box"
import CssBaseline from "@mui/material/CssBaseline"
import Divider from "@mui/material/Divider"
import Drawer from "@mui/material/Drawer"
import { TopMenuComponent } from "./TopMenuComponent"
import { DrawerContent } from "./DrawerContent"
import { COMPACT_TOP_BAR_TOTAL_HEIGHT } from "react-ui-components/components/navigation/CompactTopBarView"

export const drawerWidth = 240

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * Remove this when copying and pasting into your project.
   */
  window?: () => Window
  children: React.ReactNode
  drawerWidth?: number
}

export default function SidebarProvider({ window, children, drawerWidth: drawerWidthProp }: Props) {
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [isClosing, setIsClosing] = React.useState(false)
  const effectiveDrawerWidth = drawerWidthProp ?? drawerWidth

  const handleDrawerClose = () => {
    setIsClosing(true)
    setMobileOpen(false)
  }

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false)
  }

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen)
    }
  }

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", flex: "1 0 auto" }}>
      {/* <DrawerHeader>
        <IconButton onClick={handleDrawerClose}>
          {theme.direction === "rtl" ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </DrawerHeader> */}
      <Divider />
      {/* <Toolbar /> */}
      <DrawerContent />
      {/* <Toolbar />
      <Divider />
      <DrawerContent /> */}
    </Box>
  )

  // Remove this const when copying and pasting into your project.
  const container = window !== undefined ? () => window().document.body : undefined


  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <TopMenuComponent
        open={mobileOpen}
        drawerWidth={effectiveDrawerWidth}
        handleDrawerOpen={handleDrawerToggle}
      />

      {/* Mobile Navigation Drawer */}
      <Box
        component="nav"
        sx={{
          display: { xs: mobileOpen ? "block" : "none", sm: "none" },
          width: { sm: effectiveDrawerWidth },
          flexShrink: { sm: 0 },
        }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: effectiveDrawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Desktop Navigation Drawer */}
      <Box
        component="nav"
        sx={{
          display: { xs: "none", sm: "block" },
          width: { sm: effectiveDrawerWidth },
          flexShrink: { sm: 0 },
        }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: effectiveDrawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          minHeight: 0,
          // ml: { sm: `${effectiveDrawerWidth}px` },
        }}
      >
        <Box
          data-testid="compact-top-bar-spacer"
          data-compact-height-xs={COMPACT_TOP_BAR_TOTAL_HEIGHT.xs}
          data-compact-height-sm={COMPACT_TOP_BAR_TOTAL_HEIGHT.sm}
          sx={{
            flex: "0 0 auto",
            height: {
              xs: COMPACT_TOP_BAR_TOTAL_HEIGHT.xs,
              sm: COMPACT_TOP_BAR_TOTAL_HEIGHT.sm,
            },
          }}
        />
        {children}
      </Box>
    </Box>
  )
}
